<template>
	<div v-if="cart && rowHandlers && Object.keys(rowHandlers).length" class="checkout-wrap">
		<div :class="['sticky-cart', {'header-spacing': ! headerSpacing}]">
			<h2 class="secondary">Din varukorg</h2>
			<ul ref="checkoutCarList" :class="['cart-list', {'content-above': contentAbove, 'content-below': contentBelow}]">
				<checkoutCartRow
					v-for="(row, index) in cart.rows"
					:rowHandler="rowHandlers[getCartRowKey(row)]"
					:key="'cart-list-row-' + row.id"
					:isCustomConfig="cartContainsCustomConfig"
					@remove="remove"
				/>
			</ul>
			<voucherInput
				:cartRows="cart.rows"
				:vouchers="updatedVouchers"
				:selectedPaymentMethodId="selectedPaymentMethodId"
				@vouchers:add="val => addVoucher(val)"
				@vouchers:update="val => updateVoucher(val)"
				@vouchers:remove="val => removeVoucher(val)"
				@vouchers:clear="clearVouchers"
				@vouchers:combinationNotAllowed="moveToPaymentMethods"
			/>
			<cartCalculator
				:cart="cart"
				:vouchers="updatedVouchers"
				:orderTotalSum.sync="orderTotalSum"
				:cartSum.sync="cartSum"
			/>
		</div>
	</div>
</template>

<script>
import Vue from 'vue';
import { throttle, debounce } from 'lodash';
import store from 'datastore';
import { getCartRowKey } from 'utils';
import { mapGetters } from 'vuex';
import bus from 'eventbus';
import mq from 'mediaQuery';
import localSessionStorage from 'localSessionStorage';
import { getSelectedServiceObjects } from 'utils/cart';

const checkoutCartRow = () => import('./checkout-cart-row');
const voucherInput = () => import('./voucher-input');
const cartCalculator = () => import('./cart-calculator');

export default {
	store,
	props: {
		allowVouchers: {
			type: Boolean,
			default: true,
		},
		vouchers: {
			type: Array,
			required: true,
		},
		cartOverride: {
			type: Object,
			default: null,
		},
		selectedPaymentMethodId: {
			type: Number,
		},
	},
	data () {
		return {
			mq,
			getCartRowKey,
			unwatch: null,
			rowHandlers: null,
			updatedVouchers: [],
			headerSpacing: false,
			contentAbove: false,
			contentBelow: false,
			orderTotalSum: null,
			cartSum: null,
		};
	},
	components: {
		voucherInput,
		cartCalculator,
		checkoutCartRow,
	},
	mounted () {
		this.watch();
		this.updateRows();

		if (! this.compactView) {
			window.addEventListener('resize', this.debouncer);

			setTimeout(() => {
				for (let i = 0; i < 5; i++) {}
				this.handleCartHeight();
			},60);
		}
		const saved = localSessionStorage.get('updatedVouchers');
		if (saved) {
			this.updatedVouchers = JSON.parse(saved);
		}
		bus.$on('header.status', this.headerStatus);
	},
	beforeDestroy () {
		bus.$off('header.status', this.headerStatus);
		window.removeEventListener('resize', this.debouncer);;
		this.$refs.checkoutCarList.removeEventListener('scroll', this.handleCartScroll);

		if (this.unwatch) {
			this.unwatch();
		}
		for (const key in this.rowHandlers) {
			this.rowHandlers[key].flush();
		}
	},
	methods: {
		updateRows () {
			this.handleCartHeight();
			const trackingData = {
				source: 'Checkout Cart',
			};

			const rowHandlers = {};
			this.cart.rows.forEach((row) => {
				const key = getCartRowKey(row);
				const rowHandler = rowHandlers[key] = {
					static: false,
					quantity: row.quantity * 1,
					dirty: false,
					extraData: row.extraData || null,
					row,
					key,
				};
				if (this.cartOverride) {
					rowHandler.static = true;
					return;
				}
				const debouncer = throttle((newAmount) => {
					const oldCartRow = this.cart.rows.find(cartRow => {
						return getCartRowKey(cartRow) === key;
					});

					trackingData.coupon = true;

					if (newAmount === oldCartRow.quantity + 1) {
						trackingData.event = 'addToCart';
					} else if (newAmount === oldCartRow.quantity - 1) {
						trackingData.event = 'removeFromCart';
					}

					const data = {
						product: {
							isInsured: row.extraData.insured,
							insurance: row.insurance,
							unitPrice: row.unitPrice,
							...row.product,
						},
						amount: newAmount,
						price: row.unitPrice,
						trackingData: newAmount !== oldCartRow.quantity ? trackingData : null,
					};

					if (row.pass) {
						data.pass = row.pass;
					}
					if (rowHandler.extraData) {
						data.extraData = rowHandler.extraData;
					}
					this.$store.dispatch('cart/set', data);
					rowHandlers[key].dirty = false;
				}, 1000);
				rowHandler.flush = debouncer.flush;
				rowHandler.update = newAmount => {
					rowHandlers[key].dirty = true;
					debouncer(newAmount);
				};

				rowHandler.setExtraData = (key, value, insurance = null) => {
					// Extra data before setting the new value.
					const fromExtraData = JSON.parse(JSON.stringify(rowHandler.extraData));
					let toExtraData = JSON.parse(JSON.stringify(rowHandler.extraData));

					if (! toExtraData) {
						toExtraData = {};
					}

					if (key === 'insured') {
						trackingData.event = value ? 'addToCart' : 'removeFromCart';
						trackingData.isInsurance = true;
						trackingData.services = [insurance];
						trackingData.quantity = rowHandler.quantity;

						/** If value = true === insurance added
						 * if added -> add to service object
						 * else remove key from service object
						 */
						if (value) {
							toExtraData.services = {
								...toExtraData.services,
								[insurance.id]: value
							};
						} else {
							delete toExtraData.services?.[insurance.id];
						}
						rowHandler.extraData = toExtraData;
					} else {
						toExtraData[key] = value;
						rowHandler.extraData = toExtraData;
					}

					const data = {
						product: row.product,
						price: row.unitPrice,
						extraData: toExtraData,
						key,
						value,
						trackingData,
					};

					if (row.pass) {
						data.pass = row.pass;
					}

					this.$store.dispatch('cart/updateRow', data);
				};
			});
			if (this.rowHandlers === null) {
				this.$set(this, 'rowHandlers', rowHandlers);
			} else {
				const broken = this.mergeRows(this.rowHandlers, rowHandlers);
				console.log('broken?', broken);
				if (broken) this.$emit('outsideChange');
			}
		},
		mergeRow (oldRowHandler, newRowHandler) {
			const broken = ! oldRowHandler.dirty && oldRowHandler.quantity !== newRowHandler.quantity;
			if (! oldRowHandler.dirty) oldRowHandler.quantity = newRowHandler.quantity;
			return broken;
		},
		mergeRows (oldRowHandlers, newRowHandlers) {
			let broken = false;
			for (const key in oldRowHandlers) {
				if (newRowHandlers.hasOwnProperty(key)) {
					broken = this.mergeRow(oldRowHandlers[key], newRowHandlers[key]) || broken;
				} else {
					broken = true;
					delete oldRowHandlers[key];
				}
			}
			for (const key in newRowHandlers) {
				if (! oldRowHandlers.hasOwnProperty(key)) {
					broken = true;
					Vue.set(oldRowHandlers, key, newRowHandlers[key]);
				}
			}
			return broken;
		},
		moveToPaymentMethods () {
			this.$emit('moveToPaymentMethods');
		},
		watch () {
			if (this.unwatch) {
				this.unwatch();
			}
			this.unwatch = this.$store.watch(state => state.cart.rows, () => {
				this.updateRows();
				this.$emit('cartChanged');
			});
		},
		remove (rowHandler) {
			const row = rowHandler.row;
			const trackingData = {
				event: 'removeFromCart',
				source: 'Checkout Cart',
				quantity: rowHandler.quantity,
				coupon: true,
			};

			if (row.extraData?.services && Object.keys(row.extraData.services).length) {
				trackingData.services = getSelectedServiceObjects(row);
			}

			this.$store.dispatch('cart/remove', {
				extraData: row.extraData,
				product: {
					unitPrice: row.unitPrice,
					...row.product,
				},
				price: row.unitPrice,
				pass: row.pass,
				trackingData: trackingData,
			});
		},
		removeVoucher (voucher) {
			if (! Object.keys(voucher).length) {
				this.updatedVouchers = [];
			} else {
				const index = this.updatedVouchers.indexOf(voucher);
				if (index >= 0) {
					this.updatedVouchers.splice(index, 1);
				}
			}
		},
		updateVoucher (voucher) {
			const index = this.updatedVouchers.findIndex((value) => value.id === voucher.id);
			if (index >= 0) {
				this.$set(this.updatedVouchers, index, voucher);
			}
		},
 		clearVouchers () {
			this.updatedVouchers = [];
		},
		addVoucher (voucher) {
			if (voucher.type === 'discountCode') {
				this.updatedVouchers.unshift(voucher);
			} else {
				this.updatedVouchers.push(voucher);
			}
		},
		headerStatus (values) {
			/* values[0] is a boolean correlating to wether header is hidden or not */
			this.headerSpacing = values[0];
		},
		addCartRowCheckoutAt () {
			waja.post('cart/checkoutat')
				.data({cartId: this.cart.id})
				.go();
		},
		handleCartHeight () {
			if (! this.compactView && this.$refs.checkoutCarList) {
				if (this.$refs.checkoutCarList.scrollHeight > this.$refs.checkoutCarList.clientHeight) {
					this.handleCartScroll();
					this.$refs.checkoutCarList.addEventListener('scroll', this.handleCartScroll);
				} else {
					this.handleCartScroll();
					this.$refs.checkoutCarList.removeEventListener('scroll', this.handleCartScroll);
				}
			} else if (this.compactView && this.$refs.checkoutCarList) {
				this.$refs.checkoutCarList.removeEventListener('scroll', this.handleCartScroll);
				this.contentAbove = false;
				this.contentBelow = false;
			}
		},
		handleCartScroll () {
			if (this.$refs.checkoutCarList.scrollTop > 0) {
				this.contentAbove = true;
			} else {
				this.contentAbove = false;
			}

			if (this.$refs.checkoutCarList.scrollHeight === this.$refs.checkoutCarList.scrollTop + this.$refs.checkoutCarList.clientHeight) {
				this.contentBelow = false;
			} else {
				this.contentBelow = true;
			}
		},
		debouncer: function () {
			this.handleCartHeight();
		},
	},
	created () {
		this.addCartRowCheckoutAt();
		this.debouncer = debounce(this.debouncer, 100);
	},
	computed: {
		cart () {
			if (this.cartOverride) {
				return this.cartOverride;
			}
			return this.$store.state.cart;
		},
		cartContainsCustomConfig() {
			return this.cart.rows.some(row => row.product && row.product.id === 236123);
		},
		compactView () {
			return mq.current <= mq.md;
		},
		...mapGetters({
			isLoggedIn: 'user/isLoggedIn',
		}),
	},
	watch: {
		cart: {
			handler () {
				this.watch();
				this.updateRows();
			},
			deep: true,
		},
		updatedVouchers (to) {
			this.$emit('voucherUpdate', to);
			localSessionStorage.set('updatedVouchers', JSON.stringify(to));
		},
		vouchers (to) {
			this.updatedVouchers = to;
		},
		compactView (to) {
			if (! to) {
				window.addEventListener('resize', this.debouncer);;
			} else {
				window.removeEventListener('resize', this.debouncer);;
			}
		},
		orderTotalSum (to) {
			this.$emit('update:orderTotalSum', to);
		},
		cartSum (to) {
			this.$emit('update:cartSum', to);
		},
	},
};
</script>
